import React, { useState, useEffect } from 'react';
import { Download, FileText, Plus, Trash2, ChevronUp, ChevronDown, Edit2, RotateCcw } from 'lucide-react';

const STORAGE_KEY = 'resume_editor_data';

export default function ResumeEditor() {
  const defaultResumeData = {
    name: 'Jake Ryan',
    phone: '123-456-7890',
    email: 'jake@su.edu',
    linkedin: 'linkedin.com/in/jake',
    github: 'github.com/jake',
    sections: [
      {
        id: 'education',
        title: 'Education',
        type: 'education',
        items: [
          {
            school: 'Southwestern University',
            location: 'Georgetown, TX',
            degree: 'Bachelor of Arts in Computer Science, Minor in Business',
            dates: 'Aug. 2018 -- May 2021',
            bullets: []
          },
          {
            school: 'Blinn College',
            location: 'Bryan, TX',
            degree: "Associate's in Liberal Arts",
            dates: 'Aug. 2014 -- May 2018',
            bullets: []
          }
        ]
      },
      {
        id: 'experience',
        title: 'Experience',
        type: 'experience',
        items: [
          {
            title: 'Undergraduate Research Assistant',
            dates: 'June 2020 -- Present',
            company: 'Texas A&M University',
            location: 'College Station, TX',
            bullets: [
              'Developed a REST API using FastAPI and PostgreSQL to store data from learning management systems',
              'Developed a full-stack web application using Flask, React, PostgreSQL and Docker to analyze GitHub data',
              'Explored ways to visualize GitHub collaboration in a classroom setting'
            ]
          },
          {
            title: 'Information Technology Support Specialist',
            dates: 'Sep. 2018 -- Present',
            company: 'Southwestern University',
            location: 'Georgetown, TX',
            bullets: [
              'Communicate with managers to set up campus computers used on campus',
              'Assess and troubleshoot computer problems brought by students, faculty and staff',
              'Maintain upkeep of computers, classroom equipment, and 200 printers across campus'
            ]
          },
          {
            title: 'Artificial Intelligence Research Assistant',
            dates: 'May 2019 -- July 2019',
            company: 'Southwestern University',
            location: 'Georgetown, TX',
            bullets: [
              'Explored methods to generate video game dungeons based off of The Legend of Zelda',
              'Developed a game in Java to test the generated dungeons',
              'Contributed 50K+ lines of code to an established codebase via Git',
              'Conducted a human subject study to determine which video game dungeon generation technique is enjoyable',
              'Wrote an 8-page paper and gave multiple presentations on-campus',
              'Presented virtually to the World Conference on Computational Intelligence'
            ]
          }
        ]
      },
      {
        id: 'projects',
        title: 'Projects',
        type: 'projects',
        items: [
          {
            name: 'Gitlytics',
            technologies: 'Python, Flask, React, PostgreSQL, Docker',
            dates: 'June 2020 -- Present',
            bullets: [
              'Developed a full-stack web application using with Flask serving a REST API with React as the frontend',
              'Implemented GitHub OAuth to get data from user\'s repositories',
              'Visualized GitHub data to show collaboration',
              'Used Celery and Redis for asynchronous tasks'
            ]
          },
          {
            name: 'Simple Paintball',
            technologies: 'Spigot API, Java, Maven, TravisCI, Git',
            dates: 'May 2018 -- May 2020',
            bullets: [
              'Developed a Minecraft server plugin to entertain kids during free time for a previous job',
              'Published plugin to websites gaining 2K+ downloads and an average 4.5/5-star review',
              'Implemented continuous delivery using TravisCI to build the plugin upon new a release',
              'Collaborated with Minecraft server administrators to suggest features and get feedback about the plugin'
            ]
          }
        ]
      },
      {
        id: 'skills',
        title: 'Technical Skills',
        type: 'skills',
        items: [
          { label: 'Languages', content: 'Java, Python, C/C++, SQL (Postgres), JavaScript, HTML/CSS, R' },
          { label: 'Frameworks', content: 'React, Node.js, Flask, JUnit, WordPress, Material-UI, FastAPI' },
          { label: 'Developer Tools', content: 'Git, Docker, TravisCI, Google Cloud Platform, VS Code, Visual Studio, PyCharm, IntelliJ, Eclipse' },
          { label: 'Libraries', content: 'pandas, NumPy, Matplotlib' }
        ]
      }
    ]
  };

  // Load data from localStorage on mount
  const [resumeData, setResumeData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultResumeData;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return defaultResumeData;
    }
  });

  const [isCompiling, setIsCompiling] = useState(false);
  const [message, setMessage] = useState('');
  const [editingTitle, setEditingTitle] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Save to localStorage whenever resumeData changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(resumeData));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [resumeData]);

  // Check if user is on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Early return for mobile users - after all hooks
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8">
        <style>
          {`@import url('https://fonts.googleapis.com/css2?family=Zalando+Sans+Expanded:wght@200..700&display=swap');`}
        </style>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <FileText className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h1 
            className="text-2xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: '"Zalando Sans Expanded", system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", sans-serif' }}
          >
            Oops!
          </h1>
          <p className="text-slate-600 mb-6">
            Please use the Resume Editor on a laptop or another widescreen device. 
          </p>
          <p className="text-slate-600 mb-4">
            While you're here,{' '}
            <a
              href="https://jaydharmadhikari.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-700 hover:text-emerald-800 underline font-medium"
            >
              see my non vibe coded work
            </a>
          </p>
        </div>
      </div>
    );
  }

  const updateField = (path, value) => {
    const keys = path.split('.');
    setResumeData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    setResumeData(prev => {
      const newData = { ...prev };
      const sections = [...newData.sections];
      [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
      newData.sections = sections;
      return newData;
    });
  };

  const moveSectionDown = (index) => {
    if (index === resumeData.sections.length - 1) return;
    setResumeData(prev => {
      const newData = { ...prev };
      const sections = [...newData.sections];
      [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
      newData.sections = sections;
      return newData;
    });
  };

  const deleteSection = (index) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      setResumeData(prev => {
        const newData = { ...prev };
        newData.sections.splice(index, 1);
        return newData;
      });
    }
  };

  const addItem = (sectionIndex) => {
    setResumeData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const section = newData.sections[sectionIndex];
      
      if (section.type === 'education') {
        section.items.push({ school: '', location: '', degree: '', dates: '', bullets: [] });
      } else if (section.type === 'experience') {
        section.items.push({ title: '', dates: '', company: '', location: '', bullets: [''] });
      } else if (section.type === 'projects') {
        section.items.push({ name: '', technologies: '', dates: '', bullets: [''] });
      } else if (section.type === 'skills') {
        section.items.push({ label: '', content: '' });
      }
      return newData;
    });
  };

  const removeItem = (sectionIndex, itemIndex) => {
    setResumeData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.sections[sectionIndex].items.splice(itemIndex, 1);
      return newData;
    });
  };

  const addBullet = (sectionIndex, itemIndex) => {
    setResumeData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const section = newData.sections[sectionIndex];
      
      if (section.type === 'education') {
        section.items[itemIndex].bullets.push({ bold: '', normal: '' });
      } else {
        section.items[itemIndex].bullets.push('');
      }
      return newData;
    });
  };

  const removeBullet = (sectionIndex, itemIndex, bulletIndex) => {
    setResumeData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.sections[sectionIndex].items[itemIndex].bullets.splice(bulletIndex, 1);
      return newData;
    });
  };

  const compilePDF = async () => {
    setIsCompiling(true);
    setMessage('');
    
    try {
      const response = await fetch('https://jaydharmadhikari.com/api/resume/compile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resumeData)
      });

      if (!response.ok) {
        throw new Error('Compilation failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resume.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setMessage('PDF compiled and downloaded successfully!');
    } catch (error) {
      setMessage('Error compiling PDF: ' + error.message);
    } finally {
      setIsCompiling(false);
    }
  };

  const resetToDefault = () => {
    if (!window.confirm('Reset resume to default? This will overwrite current changes.')) return;
    const defaultClone = JSON.parse(JSON.stringify(defaultResumeData));
    setResumeData(defaultClone);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultClone));
    } catch (error) {
      console.error('Error resetting localStorage:', error);
    }
    setMessage('Resume reset to default.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Zalando+Sans+Expanded:wght@200..700&display=swap');`}
      </style>
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-emerald-600" />
              <h1
                className="text-3xl font-bold text-slate-800"
                style={{ fontFamily: '"Zalando Sans Expanded", system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", sans-serif' }}
              >
                Resume Editor
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={resetToDefault}
                className="flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-800 px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
              >
                <RotateCcw className="w-5 h-5" />
                Reset
              </button>
              <button
                onClick={compilePDF}
                disabled={isCompiling}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
              >
                <Download className="w-5 h-5" />
                {isCompiling ? 'Compiling...' : 'Download PDF'}
              </button>
            </div>
          </div>

          {/* Right-justified link under the buttons */}
          <div className="flex justify-end">
            <a
              href="https://jaydharmadhikari.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-700 hover:text-emerald-800 underline text-sm"
            >
              See my (non vibe coded) work
            </a>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg ${message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {message}
            </div>
          )}

          {/* Personal Info */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={resumeData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Full Name"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="text"
                value={resumeData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="Phone"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="email"
                value={resumeData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="Email"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="text"
                value={resumeData.linkedin}
                onChange={(e) => updateField('linkedin', e.target.value)}
                placeholder="LinkedIn URL"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="text"
                value={resumeData.github}
                onChange={(e) => updateField('github', e.target.value)}
                placeholder="GitHub URL"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </section>

          {/* Dynamic Sections */}
          {resumeData.sections.map((section, sectionIndex) => (
            <section key={section.id} className="mb-8">
              <div className="flex items-center justify-between mb-4 gap-2">
                <div className="flex items-center gap-2 flex-1">
                  {editingTitle === section.id ? (
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => updateField(`sections.${sectionIndex}.title`, e.target.value)}
                      onBlur={() => setEditingTitle(null)}
                      onKeyPress={(e) => e.key === 'Enter' && setEditingTitle(null)}
                      autoFocus
                      className="text-2xl font-bold text-slate-800 pb-2 border-b-2 border-emerald-500 focus:outline-none flex-1"
                    />
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-800 pb-2 border-b-2 border-slate-200 flex-1">
                        {section.title}
                      </h2>
                      <button
                        onClick={() => setEditingTitle(section.id)}
                        className="text-slate-500 hover:text-slate-700"
                        title="Edit section title"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => addItem(sectionIndex)}
                    className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    <Plus className="w-5 h-5" /> Add
                  </button>
                  <button
                    onClick={() => moveSectionUp(sectionIndex)}
                    disabled={sectionIndex === 0}
                    className="text-slate-500 hover:text-slate-700 disabled:text-slate-300"
                    title="Move section up"
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => moveSectionDown(sectionIndex)}
                    disabled={sectionIndex === resumeData.sections.length - 1}
                    className="text-slate-500 hover:text-slate-700 disabled:text-slate-300"
                    title="Move section down"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deleteSection(sectionIndex)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete section"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Education Section */}
              {section.type === 'education' && section.items.map((edu, i) => (
                <div key={i} className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex justify-between mb-3">
                    <span className="font-medium text-slate-700">Entry {i + 1}</span>
                    <button onClick={() => removeItem(sectionIndex, i)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      value={edu.school}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.school`, e.target.value)}
                      placeholder="School Name"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={edu.location}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.location`, e.target.value)}
                      placeholder="Location"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={edu.degree}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.degree`, e.target.value)}
                      placeholder="Degree"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={edu.dates}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.dates`, e.target.value)}
                      placeholder="Dates (e.g., Aug. 2018 -- May 2021)"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  {edu.bullets && edu.bullets.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-700">Bullet Points</label>
                        <button onClick={() => addBullet(sectionIndex, i)} className="text-sm text-emerald-600 hover:text-emerald-700">
                          + Add Bullet
                        </button>
                      </div>
                      {edu.bullets.map((bullet, j) => (
                        <div key={j} className="flex gap-2">
                          <input
                            type="text"
                            value={bullet.bold}
                            onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.bullets.${j}.bold`, e.target.value)}
                            placeholder="Bold part"
                            className="w-1/3 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                          <input
                            type="text"
                            value={bullet.normal}
                            onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.bullets.${j}.normal`, e.target.value)}
                            placeholder="Normal text"
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                          <button onClick={() => removeBullet(sectionIndex, i, j)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!edu.bullets || edu.bullets.length === 0) && (
                    <button
                      onClick={() => addBullet(sectionIndex, i)}
                      className="text-sm text-emerald-600 hover:text-emerald-700 mt-2"
                    >
                      + Add Bullet Point
                    </button>
                  )}
                </div>
              ))}

              {/* Experience Section */}
              {section.type === 'experience' && section.items.map((exp, i) => (
                <div key={i} className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex justify-between mb-3">
                    <span className="font-medium text-slate-700">Position {i + 1}</span>
                    <button onClick={() => removeItem(sectionIndex, i)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      value={exp.title}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.title`, e.target.value)}
                      placeholder="Job Title"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={exp.dates}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.dates`, e.target.value)}
                      placeholder="Dates"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={exp.company}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.company`, e.target.value)}
                      placeholder="Company"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={exp.location}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.location`, e.target.value)}
                      placeholder="Location"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">Bullet Points</label>
                      <button onClick={() => addBullet(sectionIndex, i)} className="text-sm text-emerald-600 hover:text-emerald-700">
                        + Add Bullet
                      </button>
                    </div>
                    {exp.bullets.map((bullet, j) => (
                      <div key={j} className="flex gap-2">
                        <input
                          type="text"
                          value={bullet}
                          onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.bullets.${j}`, e.target.value)}
                          placeholder="Bullet point"
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <button onClick={() => removeBullet(sectionIndex, i, j)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Projects Section */}
              {section.type === 'projects' && section.items.map((proj, i) => (
                <div key={i} className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex justify-between mb-3">
                    <span className="font-medium text-slate-700">Project {i + 1}</span>
                    <button onClick={() => removeItem(sectionIndex, i)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      value={proj.name}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.name`, e.target.value)}
                      placeholder="Project Name"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={proj.technologies}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.technologies`, e.target.value)}
                      placeholder="Technologies (comma separated)"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={proj.dates}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.dates`, e.target.value)}
                      placeholder="Dates"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent md:col-span-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">Bullet Points</label>
                      <button onClick={() => addBullet(sectionIndex, i)} className="text-sm text-emerald-600 hover:text-emerald-700">
                        + Add Bullet
                      </button>
                    </div>
                    {proj.bullets.map((bullet, j) => (
                      <div key={j} className="flex gap-2">
                        <input
                          type="text"
                          value={bullet}
                          onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.bullets.${j}`, e.target.value)}
                          placeholder="Bullet point"
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <button onClick={() => removeBullet(sectionIndex, i, j)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Skills Section */}
              {section.type === 'skills' && section.items.map((skill, i) => (
                <div key={i} className="mb-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex gap-3 items-center">
                    <input
                      type="text"
                      value={skill.label}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.label`, e.target.value)}
                      placeholder="Label (e.g., Languages)"
                      className="w-1/4 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={skill.content}
                      onChange={(e) => updateField(`sections.${sectionIndex}.items.${i}.content`, e.target.value)}
                      placeholder="Content (comma separated)"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <button onClick={() => removeItem(sectionIndex, i)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}